import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { MapPinIcon, TagIcon, CheckCircleIcon, XCircleIcon, ClockIcon } from '@heroicons/react/24/outline'
import MerchantInfo from './MerchantInfo'
import { formatDistance } from '../../../utils/GeoShippingEstimator'

const ProductInfo = ({ product, onMessageMerchant, className = '' }) => {
    const [isExpanded, setIsExpanded] = useState(false);

  if (!product) return null

  const {
    description,
    category,
    size,
    customSizeDetails,
    color,
    pickUpAddress,
    openToPickUp,
    users_permissions_user,
    brand,
    material,
    condition,
    productType
  } = product

  // Check if this is a virtual fashion item
  const isVirtual = productType === 'virtual';

  // Get color background style
  const getColorStyle = (color) => {
    const colorMap = {
      'black': { backgroundColor: '#000000' },
      'white': { backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' },
      'gray': { backgroundColor: '#6B7280' },
      'beige': { backgroundColor: '#F5F5DC', border: '1px solid #E5E7EB' },
      'brown': { backgroundColor: '#964B00' },
      'navy': { backgroundColor: '#000080' },
      'red': { backgroundColor: '#EF4444' },
      'orange': { backgroundColor: '#F97316' },
      'yellow': { backgroundColor: '#F59E0B' },
      'green': { backgroundColor: '#10B981' },
      'blue': { backgroundColor: '#3B82F6' },
      'purple': { backgroundColor: '#8B5CF6' },
      'pink': { backgroundColor: '#EC4899' },
      'gold': { backgroundColor: '#FFD700', border: '1px solid #E5E7EB' },
      'silver': { backgroundColor: '#C0C0C0', border: '1px solid #E5E7EB' },
      'multicolor': { background: 'linear-gradient(90deg, #f44336, #ff9800, #ffeb3b, #4caf50, #2196f3, #9c27b0)' },
    };

    return colorMap[color?.toLowerCase()] || { backgroundColor: '#CBD5E1' };
  };

  // Render feature as regular text instead of badge
  const renderFeature = (value) => {
    return (
      <span className="text-sm text-gray-700">
        {value}
      </span>
    );
  };

  // Render color with color dot
  const renderColorFeature = (color) => {
    return (
      <div className="flex items-center">
        <div
          className="w-4 h-4 rounded-full mr-2"
          style={getColorStyle(color)}
        />
        <span className="text-sm text-gray-700">
          {color}
        </span>
      </div>
    );
  };

  // Format condition display
  const formatCondition = (condition) => {
    if (!condition) return '';

    const conditionMap = {
      'new_with_tags': 'New with tags',
      'new_without_tags': 'New without tags',
      'like_new': 'Like new',
      'very_good': 'Very good',
      'good': 'Good',
      'fair': 'Fair'
    };

    return conditionMap[condition] || condition;
  };



  return (
    <div className={`space-y-6 w-full max-w-2xl mx-auto ${className}`}>
      {/* Description */}
      {description && (
        <div className="border-t border-gray-200 pt-6">
          <h3 className='text-sm font-medium text-gray-900'>Description</h3>
          <div className='mt-2 text-base text-gray-700 space-y-4'>
            {(() => {
              const isLong = description.length > 150;

              if (isLong && !isExpanded) {
                return (
                  <>
                    <p className="text-base text-gray-700 leading-relaxed" style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}>
                      {description}
                    </p>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => setIsExpanded(true)}
                      onKeyDown={(e) => e.key === 'Enter' && setIsExpanded(true)}
                      style={{ color: '#0d9488', cursor: 'pointer', fontWeight: 500, fontSize: '0.875rem' }}
                      onMouseEnter={(e) => e.target.style.color = '#115e59'}
                      onMouseLeave={(e) => e.target.style.color = '#0d9488'}
                    >...more</span>
                  </>
                );
              }

              return (
                <>
                  <p className="text-base text-gray-700 leading-relaxed" style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                    {description}
                  </p>
                  {isLong && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => setIsExpanded(false)}
                      onKeyDown={(e) => e.key === 'Enter' && setIsExpanded(false)}
                      style={{ color: '#0d9488', cursor: 'pointer', fontWeight: 500, fontSize: '0.875rem' }}
                      onMouseEnter={(e) => e.target.style.color = '#115e59'}
                      onMouseLeave={(e) => e.target.style.color = '#0d9488'}
                    >show less</span>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Product Notes - if available */}
      {product.notes && (
        <div className="border-t border-gray-200 pt-6">
          <h3 className='text-sm font-medium text-gray-900'>Product Notes</h3>
          <div className='mt-2 text-sm text-gray-600 space-y-2'>
            {product.notes}
          </div>
        </div>
      )}

      {/* Product Features - Hide for virtual fashion, non-launched mode, or when no details exist */}
      {!isVirtual && (category || brand || size || color || material || condition) && (
        <div className="border-t border-gray-200 pt-6">
          <h3 className='text-sm font-medium text-gray-900 mb-4'> Details</h3>

          <div className="grid grid-cols-3 gap-x-4 gap-y-6">
            {/* Category */}
            {category && (
              <div>
                <h4 className="text-xs text-gray-500 mb-2">Category</h4>
                {renderFeature(category)}
              </div>
            )}

            {/* Brand */}
            {brand && (
              <div>
                <h4 className="text-xs text-gray-500 mb-2">Brand</h4>
                {renderFeature(brand)}
              </div>
            )}

            {/* Size */}
            {size && (
              <div>
                <h4 className="text-xs text-gray-500 mb-2">Size</h4>
                {renderFeature(size === 'custom' && customSizeDetails
                  ? `${size} (${customSizeDetails})`
                  : size)}
              </div>
            )}

            {/* Color */}
            {color && (
              <div>
                <h4 className="text-xs text-gray-500 mb-2">Color</h4>
                {renderColorFeature(color)}
              </div>
            )}

            {/* Material */}
            {material && (
              <div>
                <h4 className="text-xs text-gray-500 mb-2">Material</h4>
                {renderFeature(material)}
              </div>
            )}

            {/* Condition */}
            {condition && (
              <div>
                <h4 className="text-xs text-gray-500 mb-2">Condition</h4>
                {renderFeature(formatCondition(condition))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Merchant Info */}
      {users_permissions_user && (
        <MerchantInfo
          merchant={users_permissions_user}
          onMessageMerchant={onMessageMerchant}
        />
      )}

      {/* Pickup Location - Hide for virtual fashion */}
      {!isVirtual && openToPickUp && pickUpAddress && (
        <div className='pt-6'>
          <div className='flex items-center text-sm'>
            <MapPinIcon className='h-5 w-5 text-gray-400' />
            <span className='ml-2 text-gray-700'>
              {formatDistance(pickUpAddress.distance, { country: 'US' }, { country: 'US' })}
            </span>
            {pickUpAddress.city && (
              <span className='ml-1 text-gray-500'>· {pickUpAddress.city}</span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default ProductInfo
